<script>
import storageService from "@/services/storage.service";
import Layout from "../../layouts/main";
import axios from "axios";
import serverTableMixin from "@/mixins/serverTable";
import AppIcon from "@/components/shared/AppIcon.vue";
import TableFooter from "@/components/shared/TableFooter.vue";

export default {
  name: "StaffNotification",
  mixins: [serverTableMixin],
  components: {
    Layout,
    AppIcon,
    TableFooter,
  },
  data() {
    return {
      not_count: 0,
      not_loop: [],
      loadError: false,
      searchValueTable: "",
      selectFilterSort: null,
      sortFieldMap: {},
    };
  },
  computed: {
    isLoading() {
      return this.tableLoading;
    },
  },
  created() {
    // the mixin has already read page / search from the URL
    this.loadTable();
  },
  methods: {
    // footer controls -> the server-side table options (the mixin reloads on change)
    onPageChange(page) {
      this.serverOptions = { ...this.serverOptions, page };
    },
    onRowsChange(rowsPerPage) {
      this.serverOptions = { ...this.serverOptions, rowsPerPage, page: 1 };
    },
    // clear the search box and reload the list (same behaviour as the Staff page)
    clearSearch() {
      this.searchValueTable = "";
      clearTimeout(this.searchTimer);
      this.resetAndLoad();
    },
    fetchNotifData() {
      return this.loadTable(true);
    },
    requestTable(params) {
      this.loadError = false;
      const tken = storageService.getItem("accessToken");
      // page / page_size / search go in the query string (the API reads them from there)
      return axios
        .post("/applications/kick_notif/", { token: tken, read: true }, { params })
        .catch((error) => {
          this.loadError = true;
          throw error;
        });
    },
    applyTable(data) {
      this.not_loop = data.results || [];
      this.not_count = data.unread_count || 0;
    },
  },
};
</script>

<template>
  <Layout>
    <section class="snotif">
      <!-- Heading (left) + new-count chip and search (right), on one line -->
      <header class="ecu-page-head snotif__head">
        <div>
          <h1>Notifications</h1>
          <p class="ecu-page-head__sub">Updates about the applications you work on.</p>
        </div>

        <div class="snotif__tools">
          <span v-if="!isLoading && not_count" class="ecu-chip ecu-chip--orange">
            {{ not_count }} new
          </span>
          <div class="ecu-field ecu-field--search">
            <AppIcon name="search" :size="18" />
            <input
              type="text"
              v-model="searchValueTable"
              @input="onSearchInput"
              placeholder="Search notifications..."
              aria-label="Search notifications"
              autocomplete="off"
            />
            <button
              v-if="searchValueTable"
              type="button"
              class="snotif__clear"
              aria-label="Clear search"
              @click="clearSearch"
            >
              <AppIcon name="x" :size="14" :stroke="2.4" />
            </button>
          </div>
        </div>
      </header>

      <!-- loading -->
      <div v-if="isLoading" class="snotif__list" aria-busy="true">
        <div v-for="n in 3" :key="n" class="ecu-card snotif-card">
          <span class="ecu-skeleton" style="width: 40px; height: 40px; border-radius: 12px"></span>
          <div style="flex: 1">
            <div class="ecu-skeleton" style="height: 15px; width: 40%; margin-bottom: 10px"></div>
            <div class="ecu-skeleton" style="height: 12px; width: 80%; margin-bottom: 8px"></div>
            <div class="ecu-skeleton" style="height: 10px; width: 25%"></div>
          </div>
        </div>
      </div>

      <!-- error -->
      <div v-else-if="loadError" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon ecu-state__icon--danger"><AppIcon name="alert" :size="26" /></span>
          <h3>We couldn't load your notifications</h3>
          <p>Please check your connection and try again.</p>
          <button type="button" class="ecu-btn-outline" @click="fetchNotifData">
            <AppIcon name="refresh" :size="14" /> Try again
          </button>
        </div>
      </div>

      <!-- empty -->
      <div v-else-if="not_loop.length === 0" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon"><AppIcon name="bell" :size="26" /></span>
          <h3>You're all caught up</h3>
          <p>No notifications found. New updates will show up here.</p>
        </div>
      </div>

      <!-- list -->
      <ul v-else class="snotif__list">
        <li
          v-for="(i, idx) in not_loop"
          :key="idx"
          class="ecu-card snotif-card"
          :class="{ 'snotif-card--unread': !i[3] }"
          :style="{ animationDelay: `${Math.min(idx, 8) * 40}ms` }"
        >
          <span class="ecu-tile snotif-card__icon"><AppIcon name="bell" :size="18" /></span>
          <div class="snotif-card__body">
            <h3>{{ i[0] }}</h3>
            <p v-if="i[1]">{{ i[1] }}</p>
            <time v-if="i[2]"><AppIcon name="calendar" :size="13" /> {{ i[2] }}</time>
          </div>
          <span v-if="!i[3]" class="snotif-card__dot" title="New" aria-label="New notification"></span>
        </li>
      </ul>

      <div v-if="!loadError && totalItems > 0" class="ecu-card snotif__foot">
        <TableFooter
          :page="serverOptions.page"
          :rows-per-page="serverOptions.rowsPerPage"
          :total="totalItems"
          :loading="tableLoading"
          @update:page="onPageChange"
          @update:rowsPerPage="onRowsChange"
        />
      </div>
    </section>
  </Layout>
</template>

<style scoped>
.snotif {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}
/* heading on the left, chip + search on the right, vertically aligned */
.snotif__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.snotif__tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}
.snotif__tools .ecu-field--search {
  position: relative;
  min-width: 260px;
}
.snotif__tools .ecu-field--search input {
  padding-right: 40px;
}
/* clear (X) button inside the search box */
.snotif__clear {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f6579;
  cursor: pointer;
}
.snotif__clear:hover {
  color: #e84d00;
}
.snotif__foot {
  width: 100%;
  margin-top: 8px;
  padding: 12px 18px;
}
.snotif__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}
.snotif-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 16px 20px;
  border-radius: 16px;
  overflow: hidden;
  animation: snotif-in 0.4s ease both;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.snotif-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14.4px 40px rgba(20, 40, 90, 0.1);
}
.snotif-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f3f4f9;
  color: #7b8194;
}
.snotif-card--unread {
  background: linear-gradient(100deg, #fff6ef 0%, #ffffff 60%);
}
.snotif-card--unread::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 3.2px;
  border-radius: 0 3.2px 3.2px 0;
  background: linear-gradient(180deg, #ff8a2a, #f2610f);
}
.snotif-card--unread .snotif-card__icon {
  background: var(--ecu-orange-soft, #fff1e6);
  color: var(--ecu-orange, #f97316);
}
.snotif-card__body {
  flex: 1;
  min-width: 0;
}
.snotif-card__body h3 {
  margin: 0 0 4px;
  font-size: var(--ecu-fs-h3);
  font-weight: var(--ecu-fw-subheading);
  line-height: 1.35;
  color: var(--ecu-navy, #0b1f4b);
}
.snotif-card--unread .snotif-card__body h3 {
  font-weight: var(--ecu-fw-subheading);
}
.snotif-card__body p {
  margin: 0 0 8px;
  font-size: var(--ecu-fs-body);
  line-height: 1.55;
  color: var(--ecu-muted, #5f6579);
  overflow-wrap: anywhere;
}
.snotif-card--unread .snotif-card__body p {
  color: var(--ecu-ink, #1d2a4d);
  font-weight: 600;
}
.snotif-card__body time {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--ecu-fs-small);
  color: #8b90a3;
}
.snotif-card__dot {
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: var(--ecu-orange, #f97316);
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.15);
  flex-shrink: 0;
}
@keyframes snotif-in {
  from { opacity: 0; transform: translateY(6.4px); }
  to   { opacity: 1; transform: none; }
}
@media (max-width: 767px) {
  .snotif__tools {
    width: 100%;
    margin-left: 0;
    flex-wrap: wrap;
  }
  .snotif__tools .ecu-field--search {
    flex: 1 1 100%;
    min-width: 0;
  }
}
@media (max-width: 575px) {
  .snotif-card { padding: 13px; gap: 11px; }
  .snotif-card__icon { width: 34px; height: 34px; }
}
@media (prefers-reduced-motion: reduce) {
  .snotif-card { animation: none; transition: none; }
}
</style>